import React, { useEffect, useRef } from 'react';
import { ArrowDown, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';

const tagline = "EFICIÊNCIA INFINITA.";

const HeroSection = () => {
  const heroRef = useRef(null);
  const [isHeroVisible, setIsHeroVisible] = React.useState(false);

  const [mousePos, setMousePos] = React.useState({ x: 0, y: 0 });

  useEffect(() => {
    const timer = setTimeout(() => setIsHeroVisible(true), 100);

    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const x = (clientX / innerWidth - 0.5) * 20;
      const y = (clientY / innerHeight - 0.5) * 20;
      setMousePos({ x, y });
    };

    const handleScroll = () => {
      if (heroRef.current) {
        const scrollY = window.scrollY;
        const parallaxElements = heroRef.current.querySelectorAll('.parallax');
        parallaxElements.forEach((el, index) => {
          const speed = (index + 1) * 0.1;
          el.style.transform = `translateY(${scrollY * speed}px)`;
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);

  return (
    <section
      id="hero"
      ref={heroRef}
      className={`relative min-h-screen flex items-center justify-center overflow-hidden bg-black ${isHeroVisible ? 'is-visible' : ''}`}
      style={{ perspective: '1000px' }}
    >
      {/* Cinematic Background Image Container */}
      <div
        className="absolute inset-0 z-0 overflow-hidden transition-transform duration-300 ease-out"
        style={{
          transform: `translate(${mousePos.x * -0.5}px, ${mousePos.y * -0.5}px) scale(1.1)`,
        }}
      >
        <img
          src="/img/hero-hologram.jpg"
          className="parallax w-full h-full object-cover opacity-60"
          alt="Corporate High-End Background with Hologram"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/20 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-black/40" />

        {/* Animated Bronze/Amber Glow (Complementing the image light) */}
        <div className="parallax absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-amber-600/5 rounded-full blur-[180px] animate-pulse" />
      </div>

      {/* Holographic Hexagon Grid Overlay (Option 2 - Pulse) */}
      <div
        className="absolute inset-0 z-1 opacity-5 pointer-events-none animate-holo-pulse"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='104' viewBox='0 0 60 104' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0l30 17.32v34.64L30 69.28 0 51.96V17.32L30 0z' fill='none' stroke='%23d97706' stroke-width='1'/%3E%3C/svg%3E")`,
          backgroundSize: '120px 208px',
          backgroundPosition: 'center',
          transform: `translate(${mousePos.x * 0.8}px, ${mousePos.y * 0.8}px)`
        }}
      />

      {/* Data Particles (Option 2) */}
      <div className="absolute inset-0 z-2 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 3}px`,
              height: `${Math.random() * 3}px`,
              animation: `particle-fade ${Math.random() * 5 + 3}s linear infinite`,
              animationDelay: `${Math.random() * 5}s`,
              background: Math.random() > 0.5 ? '#d97706' : '#ffffff'
            }}
          />
        ))}
      </div>

      <div
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full text-center md:text-left transition-transform duration-500 ease-out"
        style={{
          transform: `translate(${mousePos.x}px, ${mousePos.y}px) rotateY(${mousePos.x * 0.05}deg) rotateX(${mousePos.y * -0.05}deg)`,
        }}
      >
        <div className="md:w-3/5 lg:w-2/3">

          <h1 className="text-3xl sm:text-5xl lg:text-7xl font-black text-white leading-[0.9] mb-8 tracking-tighter">
            <span className="block overflow-hidden">
              <span className="block text-transparent bg-clip-text bg-gradient-to-b from-white to-slate-500 split-text-inner uppercase">Gestão da cadeia</span>
            </span>
            <span className="block relative overflow-hidden">
              <span className="block split-text-inner uppercase">de suprimentos B2B</span>
              <span className="absolute -right-8 top-0 text-amber-600 text-4xl font-light opacity-50">.</span>
            </span>
          </h1>

          <div className="flex flex-col md:flex-row gap-8 items-start mb-12">
            <div className="w-12 h-px bg-amber-600 mt-4 hidden md:block" />
            <p className="text-lg sm:text-xl text-slate-400 max-w-2xl leading-relaxed overflow-hidden">
              <span className="block split-text-inner">
                Há mais de 10 anos simplificamos a cadeia de suprimentos das maiores empresas do país com soluções completas que garantem a continuidade e a excelência do seu negócio.
              </span>
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-8 animate-fade-in-up animation-delay-300">
            <a href="#contato">
              <Button
                size="lg"
                className="bg-white text-black hover:bg-amber-600 hover:text-white px-10 py-8 text-sm font-bold rounded-none transition-all duration-500 hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] uppercase tracking-[0.2em]"
              >
                Solicitar Cotação
              </Button>
            </a>
            <a href="#produtos" className="group flex items-center gap-4 text-slate-400 hover:text-white transition-all">
              <span className="text-sm font-bold uppercase tracking-[0.2em]">Explorar Portfólio</span>
              <div className="w-8 h-px bg-slate-700 group-hover:w-16 group-hover:bg-amber-500 transition-all duration-500" />
            </a>
          </div>
        </div>
      </div>


      {/* Bottom Vignette */}
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-black to-transparent pointer-events-none" />
    </section>
  );
};

export default HeroSection;
